import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import { Link as RouterLink } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import HeroSection from '@/components/HeroSection';
import ProfessionTicker from '@/components/ProfessionTicker';
import MissionSection from '@/components/MissionSection';
import Features from '@/components/Features';
import PracticeShiftSection from '@/components/PracticeShiftSection';
import TargetAudience from '@/components/TargetAudience';
import landingLogo from '@/assets/images/landing/logo_2.svg';
import { openCookiePreferences } from '@/lib/cookieConsent';
import { recordLandingOperationalLocationVisit } from '@/lib/landingOperations';
import styles from './StartPage.module.css';

const StartStickyCta: React.FC = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const hero = document.getElementById('start-hero');
    if (!hero) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setVisible(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '0px' }
    );

    observer.observe(hero);
    return () => observer.disconnect();
  }, []);

  return (
    <RouterLink
      to="/signup"
      className={`${styles.stickyBar} ${visible ? styles.stickyBarVisible : ''}`}
      aria-label="Start Free. 31 days, no card"
      data-analytics-event="Landing CTA Clicked"
      data-analytics-id="start_sticky_get_started"
      data-analytics-label="Start Free · 31 days, no card"
      data-analytics-section="sticky_cta"
    >
      <span className={styles.stickyText}>
        Start Free <span className={styles.stickySep} aria-hidden="true">·</span> 31 days, no card
      </span>
    </RouterLink>
  );
};

const StartClosingCta: React.FC = () => {
  return (
    <section className={styles.closing} aria-labelledby="start-closing-heading">
      <div className={styles.closingInner}>
        <h2 id="start-closing-heading" className={styles.closingHeadline}>
          More clients. The same care.
          <br />
          More time for the work you love.
        </h2>
        <p className={styles.closingNote}>31 days free. No credit card. Cancel anytime.</p>
        <Button
          component={RouterLink}
          to="/signup"
          className={styles.closingButton}
          data-analytics-event="Landing CTA Clicked"
          data-analytics-id="start_closing_get_started"
          data-analytics-label="Start Free"
          data-analytics-section="closing_cta"
        >
          Start Free
        </Button>
      </div>
    </section>
  );
};

const StartFooter: React.FC = () => {
  return (
    <footer className={styles.footer}>
      <div className={styles.footerInner}>
        <RouterLink to="/" className={styles.footerBrand}>
          <img src={landingLogo} alt="CalorieScience" className={styles.footerLogo} />
        </RouterLink>
        <nav className={styles.footerLinks} aria-label="Legal">
          <RouterLink to="/privacy">Privacy Policy</RouterLink>
          <RouterLink to="/terms">Terms and Conditions</RouterLink>
          <RouterLink to="/cookies">Cookie Policy</RouterLink>
          <button type="button" onClick={openCookiePreferences}>
            Cookie Preferences
          </button>
        </nav>
        <p className={styles.footerCopy}>
          &copy; {new Date().getFullYear()} CalorieScience. All rights reserved.
        </p>
      </div>
    </footer>
  );
};

const StartPage: React.FC = () => {
  useEffect(() => {
    recordLandingOperationalLocationVisit();
  }, []);

  useEffect(() => {
    const stripHash = () => {
      if (!window.location.hash) return;
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    };
    stripHash();
    window.addEventListener('hashchange', stripHash);
    return () => window.removeEventListener('hashchange', stripHash);
  }, []);

  return (
    <div id="top" className={styles.page}>
      <Navbar />
      <main className={styles.main}>
        <HeroSection variant="start" />
        <ProfessionTicker />
        <section id="about-us" className="scroll-mt-24">
          <MissionSection />
        </section>
        <Features showWorkflowTabs={false} showOutcomeText={false} />
        <PracticeShiftSection variant="sync" />
        <TargetAudience />
        <StartClosingCta />
      </main>
      <StartFooter />
      <StartStickyCta />
    </div>
  );
};

export default StartPage;
